/**
 * BoardUtils - Thin wrapper around chessops for mini-game move calculation
 *
 * This provides simple functions for:
 * - Getting attack squares for pieces
 * - Parsing FEN into piece positions
 * - Making moves on a board
 *
 * Unlike a full chess engine, this doesn't care about:
 * - Legal moves (check, pins, etc.)
 * - Turn management
 * - Game ending conditions
 */
import { parseFen } from "chessops/fen";
import { SquareSet } from "chessops/squareSet";
import { rookAttacks, bishopAttacks, queenAttacks, knightAttacks, kingAttacks, pawnAttacks, } from "chessops/attacks";
import { parseSquare, makeSquare } from "chessops/util";
// Type conversions
const ROLE_TO_TYPE = {
    king: "k",
    queen: "q",
    rook: "r",
    bishop: "b",
    knight: "n",
    pawn: "p",
};
function toChessopsColor(color) {
    return color === "w" ? "white" : "black";
}
function fromChessopsColor(color) {
    return color === "white" ? "w" : "b";
}
/**
 * Parse a FEN string into board state.
 * Works with any FEN, including positions without kings.
 */
export function parseFenToBoard(fen) {
    const result = parseFen(fen);
    if (!result.isOk) {
        console.error("Failed to parse FEN:", fen);
        return null;
    }
    const setup = result.value;
    const pieces = [];
    // Extract all pieces from the board
    for (let sq = 0; sq < 64; sq++) {
        const piece = setup.board.get(sq);
        if (piece) {
            pieces.push({
                type: ROLE_TO_TYPE[piece.role],
                color: fromChessopsColor(piece.color),
                square: makeSquare(sq),
            });
        }
    }
    return { fen, pieces };
}
/**
 * Get squares a piece can attack from a given square.
 *
 * This returns RAW ATTACK PATTERNS:
 * - No check/pin consideration (perfect for mini-games)
 * - For sliding pieces, respects blocking by other pieces
 * - For pawns, returns diagonal attack squares only
 */
export function getAttackSquares(square, pieceType, pieceColor, allPieces) {
    const sq = parseSquare(square);
    if (sq === undefined)
        return [];
    // Build occupied squares set for sliding piece calculations
    let occupied = SquareSet.empty();
    for (const p of allPieces) {
        const psq = parseSquare(p.square);
        if (psq !== undefined) {
            occupied = occupied.with(psq);
        }
    }
    // Get attack pattern based on piece type
    let attackSet;
    switch (pieceType) {
        case "n":
            attackSet = knightAttacks(sq);
            break;
        case "b":
            attackSet = bishopAttacks(sq, occupied);
            break;
        case "r":
            attackSet = rookAttacks(sq, occupied);
            break;
        case "q":
            attackSet = queenAttacks(sq, occupied);
            break;
        case "k":
            attackSet = kingAttacks(sq);
            break;
        case "p":
            attackSet = pawnAttacks(toChessopsColor(pieceColor), sq);
            break;
        default:
            return [];
    }
    // Convert to square names
    const squares = [];
    for (const destSq of attackSet) {
        squares.push(makeSquare(destSq));
    }
    return squares;
}
/**
 * Get valid move destinations for a piece (attacks minus own pieces).
 * Optionally include pawn pushes.
 */
export function getValidMoveSquares(square, pieceType, pieceColor, allPieces, options) {
    const attacks = getAttackSquares(square, pieceType, pieceColor, allPieces);
    // Filter out own pieces
    const ownPieceSquares = new Set(allPieces.filter((p) => p.color === pieceColor).map((p) => p.square));
    let validSquares = attacks.filter((s) => !ownPieceSquares.has(s));
    // Add pawn pushes if requested
    if (pieceType === "p" && options?.includePawnPushes) {
        const pushes = getPawnPushSquares(square, pieceColor, allPieces);
        validSquares = [...validSquares, ...pushes];
    }
    return validSquares;
}
/**
 * Get pawn push squares (non-capture moves).
 */
export function getPawnPushSquares(square, pieceColor, allPieces) {
    const sq = parseSquare(square);
    if (sq === undefined)
        return [];
    const file = sq % 8;
    const rank = Math.floor(sq / 8);
    const direction = pieceColor === "w" ? 1 : -1;
    const startRank = pieceColor === "w" ? 1 : 6;
    const occupiedSet = new Set(allPieces.map((p) => p.square));
    const pushes = [];
    // Single push
    const singlePushRank = rank + direction;
    if (singlePushRank >= 0 && singlePushRank <= 7) {
        const singlePushSq = singlePushRank * 8 + file;
        const singlePushSquare = makeSquare(singlePushSq);
        if (!occupiedSet.has(singlePushSquare)) {
            pushes.push(singlePushSquare);
            // Double push from starting rank
            if (rank === startRank) {
                const doublePushRank = rank + 2 * direction;
                const doublePushSq = doublePushRank * 8 + file;
                const doublePushSquare = makeSquare(doublePushSq);
                if (!occupiedSet.has(doublePushSquare)) {
                    pushes.push(doublePushSquare);
                }
            }
        }
    }
    return pushes;
}
/**
 * Make a move and return the new board state.
 * Does NOT validate the move - caller is responsible for that.
 */
export function makeMove(pieces, from, to, promotion) {
    const newPieces = [...pieces];
    let captured = null;
    // Find the piece to move
    const movingPieceIndex = newPieces.findIndex((p) => p.square === from);
    if (movingPieceIndex === -1) {
        return { pieces: newPieces, captured: null };
    }
    const movingPiece = { ...newPieces[movingPieceIndex] };
    // Check for capture
    const capturedIndex = newPieces.findIndex((p) => p.square === to);
    if (capturedIndex !== -1) {
        captured = newPieces[capturedIndex];
        newPieces.splice(capturedIndex, 1);
    }
    // Update piece position
    const updatedIndex = newPieces.findIndex((p) => p.square === from);
    if (updatedIndex !== -1) {
        newPieces[updatedIndex] = {
            ...movingPiece,
            square: to,
            type: promotion || movingPiece.type,
        };
    }
    return { pieces: newPieces, captured };
}
/**
 * Convert pieces array back to FEN string.
 */
export function piecesToFen(pieces, activeColor = "w") {
    // Build board array
    const board = Array(8)
        .fill(null)
        .map(() => Array(8).fill(null));
    for (const piece of pieces) {
        const sq = parseSquare(piece.square);
        if (sq === undefined)
            continue;
        const file = sq % 8;
        const rank = Math.floor(sq / 8);
        const char = piece.color === "w" ? piece.type.toUpperCase() : piece.type.toLowerCase();
        board[7 - rank][file] = char;
    }
    // Convert to FEN piece placement
    const ranks = [];
    for (const row of board) {
        let rankStr = "";
        let emptyCount = 0;
        for (const cell of row) {
            if (cell === null) {
                emptyCount++;
            }
            else {
                if (emptyCount > 0) {
                    rankStr += emptyCount;
                    emptyCount = 0;
                }
                rankStr += cell;
            }
        }
        if (emptyCount > 0) {
            rankStr += emptyCount;
        }
        ranks.push(rankStr);
    }
    return `${ranks.join("/")} ${activeColor} - - 0 1`;
}
/**
 * Get a piece at a specific square.
 */
export function getPieceAt(pieces, square) {
    return pieces.find((p) => p.square === square) || null;
}
/**
 * Get all pieces of a specific color.
 */
export function getPiecesByColor(pieces, color) {
    return pieces.filter((p) => p.color === color);
}
/**
 * Get all pieces of a specific type and color.
 */
export function getPiecesByType(pieces, type, color) {
    return pieces.filter((p) => p.type === type && (color === undefined || p.color === color));
}
export function isValidSquare(square) {
    return /^[a-h][1-8]$/.test(square);
}
